import React, { useState, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useHistory } from "react-router-dom";
import { parse } from "query-string";

import { GetIconButton } from "./styles";
import KeepEditsWarning from "./KeepEditsWarning";
import IconDetailDrawer from "icons/IconDetailDrawer";
import IconDetailDownload from "icons/IconDetailDownload";

import { ICON_CHECKOUT, authLinks } from "data/links";
import { IconEditorStateContext } from "js/contexts/IconEditorContext";
import { UploadLicense } from "js/graphql";
import {
    QUERY_OPTIONS,
    QUERY_PURCHASED,
    PRO_ANNUAL_DOWNLOAD,
    PRO_SINGLE_DOWNLOAD,
    PRO_PREPAID_DOWNLOAD,
    FREE_DOWNLOAD,
    getValidKey
} from "icons/helpers";
import breakpoints from "core/breakpoints";
import { useViewport } from "js/hooks/useViewport";

const STEP_GET_ICON = 1;
const STEP_SELECT_OPTION = 2;
const STEP_FREE = 3;
const STEP_PURCHASED = 4;

const IconDetailSteps = ({
    icon,
    userIsLoggedIn = false,
    userHasSubscription = false,
    userHasPrepaidBalance = false,
    hasEditedIcon = false
}) => {
    const { id: iconId, licenseDescription: iconLicense } = icon;
    const history = useHistory();

    const queryString = useLocation().search;
    const {
        [QUERY_PURCHASED]: showPurchased,
        [QUERY_OPTIONS]: showOptions
    } = parse(queryString);
    const initialStep = showPurchased
        ? STEP_PURCHASED
        : showOptions
        ? STEP_SELECT_OPTION
        : STEP_GET_ICON;
    const [step, setStep] = useState(initialStep);

    const [showEditWarningFor, setShowEditWarning] = useState(null);

    const { browserWidth } = useViewport();
    const onMobile = browserWidth < breakpoints.tablet.px;

    // To mitigate users bookmarking steps 2 & 3, we remove the query from the URL.
    // (This uses the same pattern as PhotoDetailSteps; see for details)
    const [initialSelection] = useState(
        getValidKey(showOptions) || PRO_ANNUAL_DOWNLOAD
    );
    useLayoutEffect(() => {
        if (showOptions || showPurchased) {
            history.replace(location.pathname);
        }
    }, []);

    // Show download views for -
    // existing subscription, purchased sub., purchased single, prepaid, basic, & public
    const downloadTypes = {
        isPurchased: step === STEP_PURCHASED,
        isPublic: iconLicense === UploadLicense.Publicdomain,
        isBasic: step === STEP_FREE,
        isPro: userHasSubscription
    };
    const showDownloadView = Object.values(downloadTypes).some(value => value);
    if (userIsLoggedIn && showDownloadView) {
        return (
            <IconDetailDownload
                {...downloadTypes}
                icon={icon}
                onBack={() => {
                    setStep(STEP_SELECT_OPTION);
                }}
            />
        );
    }

    return (
        <>
            <IconDetailDrawer
                show={step === STEP_SELECT_OPTION && userIsLoggedIn}
                icon={icon}
                initialSelection={initialSelection}
                hasEditedIcon={hasEditedIcon}
                onClose={() => setStep(STEP_GET_ICON)}
                onSubmit={checkoutType => {
                    if (
                        hasEditedIcon &&
                        !onMobile &&
                        (checkoutType === FREE_DOWNLOAD ||
                            checkoutType === PRO_SINGLE_DOWNLOAD)
                    ) {
                        setShowEditWarning(checkoutType);
                        setStep(STEP_GET_ICON);
                        return;
                    }
                    if (checkoutType === FREE_DOWNLOAD) {
                        setStep(STEP_FREE);
                        return;
                    }
                    // navigate to checkout page
                    history.push(
                        ICON_CHECKOUT.replace(
                            ":checkoutType",
                            checkoutType
                        ).replace(":iconId", iconId)
                    );
                }}
            />

            <KeepEditsWarning
                show={!!showEditWarningFor}
                iconId={iconId}
                onClose={() => setShowEditWarning(null)}
                onLoseEditsClick={
                    showEditWarningFor === FREE_DOWNLOAD
                        ? {
                              onClick() {
                                  setStep(STEP_FREE);
                                  setShowEditWarning(null);
                              }
                          }
                        : {
                              href: ICON_CHECKOUT.replace(
                                  ":checkoutType",
                                  showEditWarningFor
                              ).replace(":iconId", iconId)
                          }
                }
            />

            <GetIconButton
                isDisabled={step === STEP_SELECT_OPTION && userIsLoggedIn}
                onClick={() => {
                    // require the user is logged in
                    if (!userIsLoggedIn) {
                        const postSignupUrl = `${location.pathname}?${QUERY_OPTIONS}=${initialSelection}`;
                        const nextUrl = encodeURIComponent(postSignupUrl);
                        const djangoSignupUrl = `${authLinks.signup.href}?next=${nextUrl}`;
                        // Not using history.push because signup URL is not part of icons router
                        window.location.assign(djangoSignupUrl);
                    } else {
                        setStep(STEP_SELECT_OPTION);
                    }
                }}
            >
                Get This Icon
            </GetIconButton>
        </>
    );
};

IconDetailSteps.propTypes = {
    icon: PropTypes.object.isRequired,
    userIsLoggedIn: PropTypes.bool,
    userHasSubscription: PropTypes.bool,
    userHasPrepaidBalance: PropTypes.bool,
    hasEditedIcon: PropTypes.bool
};

export default props => (
    <IconEditorStateContext.Consumer>
        {state => (
            <IconDetailSteps
                {...props}
                hasEditedIcon={
                    state[props.iconId] && state[props.iconId].hasBeenEdited
                }
            />
        )}
    </IconEditorStateContext.Consumer>
);
